import { ComponentProps, useMemo } from 'react';

import { MyFavoriteCarouselCard } from 'core/Components';
import { useBreakPoints } from 'core/Hooks';
import { useNavigate } from 'react-router-dom';
import { useListStoreItems } from 'store/Api';
import {
  Button,
  DEFAULT_IMAGE_PLACEHOLDER,
  DEFAULT_PTA_IMAGE,
  Group,
  Stack,
  Text,
  Title,
  centsToDollar,
} from 'ui';

import { useCausePageContext } from '../../CauseContext';

const AboutMemberships = () => {
  const { cause } = useCausePageContext();

  const { isSmallScreen } = useBreakPoints();
  const navigate = useNavigate();

  const { data: membershipProductsData } = useListStoreItems({
    queryOptions: {
      order: { field: 'name', direction: 'asc' },
      options: {
        total: true,
      },
      searches: {
        type: {
          value: 'StoreItems::Membership',
        },
        in_store: {
          value: true,
        },
        live: {
          value: true,
        },
      },
      join: 'AND',
    },
    pagination: {
      limit: 5,
    },
    causeId: cause.uuid,
  });

  const carouselItems = useMemo(() => {
    if (!membershipProductsData) return [];

    return membershipProductsData.store_items.map((membership) => {
      const showPTADefaultImage = cause?.in_state_pta && !membership.images[0];

      return {
        name: membership.name,
        image: showPTADefaultImage
          ? DEFAULT_PTA_IMAGE
          : (membership.images[0]?.url ?? DEFAULT_IMAGE_PLACEHOLDER),
        onClick: () => navigate(`../shop/items/${membership.uuid}`),
        subtitle: (
          <Text fz="xxxs" fw={600} c="mint.7" truncate>
            {centsToDollar(membership.price)}
          </Text>
        ),
      } as ComponentProps<typeof MyFavoriteCarouselCard>['items'][number];
    });
  }, [membershipProductsData, navigate, cause]);

  if (!membershipProductsData?.store_items.length) {
    return null;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2} c="gray.8">
          Memberships
        </Title>

        <Button variant="subtle" onClick={() => navigate(`../shop?category=memberships`)} size="sm">
          View All ({membershipProductsData?.meta.total})
        </Button>
      </Group>

      <MyFavoriteCarouselCard
        hideHeader
        items={carouselItems}
        numTotalItems={membershipProductsData?.meta.total}
        maxVisibleItems={5}
        onViewAllClick={() => navigate(`../shop?category=memberships`)}
        aspectRatio={3 / 2}
        alwaysShowInCarousel
        slidesToScroll={isSmallScreen ? 2 : 3}
      />
    </Stack>
  );
};

export { AboutMemberships };
